// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Widgets\Header\AutocompleteProducts.tsx
 * ****** */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import searchResultProps from "@store/Themes/common/props/searchResults";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // Store Custom Code
    link?: InjectableCss;
    erpNumberWrapper?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    /* Store Custom Code - Start */
    headerText: searchResultProps.title,
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            position: relative;

            &:hover {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: ${pxToRem(5)} 0;
            cursor: pointer;
            background-color: ${getColor("common.accent")};
        `,
    },
    /* Store Custom Code - End */
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
            justify-content: center; /*  Store Custom Code */
        `,
    },
    /* Store Custom Code - Start */
    titleLink: searchResultProps.link,
    link: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    erpNumberWrapper: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    /* Store Custom Code - End */
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;

        /* Store Custom Code - Start */
        const titleLinkStyles = css`
            ${styles.titleLink?.css}

            ${TypographyStyle} {
                margin: 0;
            }
        `;
        /* Store Custom Code - End */

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                            <LazyImage {...styles.image} src={product.image} className="autocomplete-products-image" />
                        </GridItem>
                        <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link
                                {...styles.titleLink}
                                css={titleLinkStyles} // Store Custom Code
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                {/* Store Custom Code - Start */}
                                <StyledWrapper {...styles.link}>{product.displayTitle}</StyledWrapper>
                                {/* Store Custom Code - End */}
                            </Link>
                            {/* Store Custom Code - Start */}
                            <StyledWrapper {...styles.erpNumberWrapper}>
                                {product.isNameCustomerOverride && (
                                    <>
                                        {translate("CLIENT PART #")}: &nbsp;
                                        <Typography
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            {product.name}
                                        </Typography>
                                    </>
                                )}

                                {!product.isNameCustomerOverride && (
                                    <>
                                        {translate("BRS SKU #")}: &nbsp;
                                        <Typography
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            {product.displayErpNumber}
                                        </Typography>
                                    </>
                                )}
                            </StyledWrapper>
                            {/* Store Custom Code - End */}
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
